import React, { useMemo } from 'react';
import { InstalledCapacityResponse, DataType } from '../../../services/powerService';

interface InstalledCapacitySummaryProps {
  capacityData: InstalledCapacityResponse;
  dataType: DataType;
}

// Group sources into categories
const sourceCategories = {
  renewable: [
    'solar',
    'wind_onshore',
    'wind_offshore',
    'biomass',
    'geothermal',
    'hydro_run_of_river',
    'hydro_water_reservoir',
    'marine',
    'other_renewables',
  ],
  conventional: [
    'nuclear',
    'fossil_brown_coal',
    'fossil_hard_coal',
    'fossil_gas',
    'fossil_oil',
    'fossil_coal_derived_gas',
    'fossil_oil_shale',
    'fossil_peat',
    'waste',
    'other_non_renewable',
  ],
  storage: ['hydro_pumped_storage', 'battery_storage_power'],
};

const InstalledCapacitySummary: React.FC<InstalledCapacitySummaryProps> = ({
  capacityData,
  dataType,
}) => {
  // Calculate key metrics from data
  const metrics = useMemo(() => {
    if (!capacityData || capacityData.data.length === 0) {
      return null;
    }

    // Get the latest data point for current view
    const latestData = capacityData.data[capacityData.data.length - 1];

    // Calculate totals for each category
    const getTotalForCategory = (category: string[], data: any): number => {
      return category.reduce((sum, key) => {
        const value = data[key as keyof typeof data];
        return sum + (typeof value === 'number' ? value : 0);
      }, 0);
    };

    const renewableCapacity = getTotalForCategory(sourceCategories.renewable, latestData);
    const conventionalCapacity = getTotalForCategory(sourceCategories.conventional, latestData);
    const storageCapacity = getTotalForCategory(sourceCategories.storage, latestData);
    const totalCapacity = renewableCapacity + conventionalCapacity;

    // Calculate renewable percentage
    const renewablePercentage = totalCapacity > 0 ? (renewableCapacity / totalCapacity) * 100 : 0;

    // For yearly data, calculate growth
    let yearlyGrowth = null;
    if (capacityData.data.length > 1 && dataType === 'total') {
      const previousData = capacityData.data[capacityData.data.length - 2];
      const previousTotal = getTotalForCategory(
        [...sourceCategories.renewable, ...sourceCategories.conventional],
        previousData,
      );
      yearlyGrowth = {
        absolute: totalCapacity - previousTotal,
        percentage: previousTotal > 0 ? ((totalCapacity - previousTotal) / previousTotal) * 100 : 0,
      };
    }

    // Get top sources
    const allSources = [...sourceCategories.renewable, ...sourceCategories.conventional];

    const topSources = allSources
      .map((key) => ({
        key,
        value: (latestData[key as keyof typeof latestData] as number) || 0,
        isRenewable: sourceCategories.renewable.includes(key),
      }))
      .filter((item) => item.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    // Format source names for display
    const formatSourceName = (key: string): string => {
      return key
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
        .replace('Fossil ', '');
    };

    return {
      country: capacityData.country_name,
      countryCode: capacityData.country_code,
      granularity: capacityData.granularity,
      dataType: capacityData.data_type,
      renewableCapacity,
      conventionalCapacity,
      storageCapacity,
      totalCapacity,
      renewablePercentage,
      yearlyGrowth,
      topSources: topSources.map((source) => ({
        ...source,
        name: formatSourceName(source.key),
      })),
    };
  }, [capacityData, dataType]);

  if (!metrics) {
    return <div>No data available</div>;
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
        {metrics.country} ({metrics.countryCode}) -{' '}
        {dataType === 'total' ? 'Installed Capacity' : 'Capacity Changes'}
      </h3>

      {dataType === 'total' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Capacity Card */}
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Total Installed Capacity
            </h4>
            <div className="mt-3">
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {metrics.totalCapacity.toFixed(2)} <span className="text-sm font-normal">GW</span>
              </p>
              {metrics.yearlyGrowth && (
                <p
                  className={`text-sm mt-1 ${metrics.yearlyGrowth.absolute >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
                >
                  {metrics.yearlyGrowth.absolute >= 0 ? '+' : ''}
                  {metrics.yearlyGrowth.absolute.toFixed(2)} GW (
                  {metrics.yearlyGrowth.percentage >= 0 ? '+' : ''}
                  {metrics.yearlyGrowth.percentage.toFixed(1)}%)
                </p>
              )}
            </div>
          </div>

          {/* Renewable Capacity Card */}
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Renewable Capacity
            </h4>
            <div className="mt-3">
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {metrics.renewableCapacity.toFixed(2)}{' '}
                <span className="text-sm font-normal">GW</span>
              </p>
              <p className="text-sm mt-1 text-green-600 dark:text-green-400">
                {Math.round(metrics.renewablePercentage)}% of total capacity
              </p>
            </div>
          </div>

          {/* Conventional Capacity Card */}
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Conventional Capacity
            </h4>
            <div className="mt-3">
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {metrics.conventionalCapacity.toFixed(2)}{' '}
                <span className="text-sm font-normal">GW</span>
              </p>
              <p className="text-sm mt-1 text-gray-600 dark:text-gray-400">
                {Math.round(100 - metrics.renewablePercentage)}% of total capacity
              </p>
            </div>
          </div>

          {/* Storage Capacity Card */}
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Storage Capacity
            </h4>
            <div className="mt-3">
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {metrics.storageCapacity.toFixed(2)} <span className="text-sm font-normal">GW</span>
              </p>
              <p className="text-sm mt-1 text-gray-600 dark:text-gray-400">
                Energy storage and balancing
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Show top 5 sources for both total and delta */}
      <div className="mt-6">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Top 5 Power Sources {dataType === 'delta' ? '(Changes)' : ''}
        </h4>
        <div className="space-y-3">
          {metrics.topSources.map((source) => (
            <div key={source.key} className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <div
                    className={`w-3 h-3 rounded-full mr-2 ${source.isRenewable ? 'bg-green-500' : 'bg-gray-500'}`}
                  ></div>
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    {source.name}
                  </span>
                </div>
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {source.value.toFixed(2)} GW
                </span>
              </div>
              <div className="mt-2">
                <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${source.isRenewable ? 'bg-green-500' : 'bg-gray-500'}`}
                    style={{ width: `${(source.value / metrics.totalCapacity) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default InstalledCapacitySummary;
